import {
  ATTENDANCE_CELL_STATUS_LABELS,
  DAY_MARK_KIND_LABELS,
  DAY_MARK_SYMBOLS,
  fmtOtBadge,
  formatMoney,
  PAYSLIP_EXTRA_KEYS,
  PAYSLIP_EXTRA_LABELS,
  PAYSLIP_LEAVE_REASON_LABELS,
  WEEKDAY_SHORT,
  weekdayOf,
  type AttendanceCellStatus,
  type DayMarkKind,
  type PayslipSheet,
} from '@mt/shared';
import { cn } from '@/lib/utils';
import { CELL_CLASS, fmtWork } from './attendance-ui';
import type { LineItemView } from './payroll-api';

const WEEK_MON_FIRST = [1, 2, 3, 4, 5, 6, 0];

/** Màu chữ ký hiệu trạng thái ngày (Đ Xuyên đêm · T Về trễ · H Hỗ trợ · S Đi sớm) – có chữ nên màu không là tín hiệu duy nhất */
const MARK_CLASS: Record<DayMarkKind, string> = {
  overnight: 'bg-info text-white',
  late_return: 'bg-warning text-white',
  support: 'bg-primary text-primary-foreground',
  early: 'bg-success text-white',
};

/** Chú thích ký hiệu ô (dùng chung "Bảng công của tôi" và phiếu lương). */
export function AttendanceLegend({ marks }: { marks?: boolean }) {
  const item = (st: AttendanceCellStatus, sym: string, text: string) => (
    <span className="inline-flex items-center gap-1">
      <span className={cn('inline-flex h-4 min-w-5 items-center justify-center rounded px-0.5 text-[10px]', CELL_CLASS[st])}>{sym}</span>
      {text}
    </span>
  );
  return (
    <div className="flex flex-wrap gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
      {item('full', '1', 'đủ công')}
      {item('half', '0,5', 'nửa công')}
      {item('leave', 'P', 'phép')}
      {item('leave_unpaid', 'KL', 'không lương')}
      {item('sunday_work', 'LCN', 'làm CN')}
      {item('absent', 'V', 'vắng')}
      {item('late', 'Trễ', 'trễ / về sớm')}
      {item('problem', '!', 'cần xem')}
      {marks && (
        <>
          <span className="inline-flex items-center gap-1">
            <span className="text-[10px] font-semibold text-warning">+2h</span>tăng ca
          </span>
          {(['overnight', 'late_return', 'support', 'early'] as DayMarkKind[]).map((k) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span className={cn('inline-flex size-4 items-center justify-center rounded-sm text-[9px] font-bold', MARK_CLASS[k])}>{DAY_MARK_SYMBOLS[k]}</span>
              {k === 'overnight' ? 'qua đêm' : DAY_MARK_KIND_LABELS[k].toLowerCase()}
            </span>
          ))}
        </>
      )}
    </div>
  );
}

/**
 * DEC-950 – tờ công trên phiếu lương: mục Công (ngày công, P, KL, V), phép năm tháng này (+1 / 0), lịch ngày giống
 * "Bảng công của tôi" có đánh dấu Tăng ca / Đ / T / H / S, rồi 4 dòng Tăng ca · Về trễ · Hỗ trợ · Qua đêm kèm ghi chú từng ngày.
 * `items` = khoản lương của phiếu, để lấy tiền tăng ca NV văn phòng (khoản theo giờ).
 */
export function PayslipSheetView({ sheet, items, live }: { sheet: PayslipSheet; items: readonly LineItemView[]; live?: boolean }) {
  const lead = sheet.days.length ? WEEK_MON_FIRST.indexOf(weekdayOf(sheet.days[0]!.d)) : 0;
  // NV văn phòng: tiền tăng ca = khoản tính theo giờ (khoản tăng ca, ngoài giờ)
  const officeOt = items
    .filter((i) => i.kind === 'earning' && i.calc === 'by_hour' && (i.autoSource === null || i.autoSource === 'after_hours'))
    .reduce((a, i) => a + i.amount, 0);
  const tiles: { sym: string; label: string; value: string; st: AttendanceCellStatus; on: boolean }[] = [
    { sym: 'Công', label: 'Ngày công', value: `${fmtWork(Math.max(0, sheet.work - sheet.p))}/${fmtWork(sheet.std)}`, st: 'full', on: true },
    { sym: 'P', label: 'Ngày phép', value: fmtWork(sheet.p), st: 'leave', on: sheet.p > 0 },
    { sym: 'KL', label: 'Không lương', value: fmtWork(sheet.kl), st: 'leave_unpaid', on: sheet.kl > 0 },
    { sym: 'V', label: 'Vắng', value: fmtWork(sheet.absent), st: 'absent', on: sheet.absent > 0 },
  ];
  const lv = sheet.leave;
  return (
    <div className="space-y-3" data-testid="payslip-sheet">
      <section className="space-y-2">
        <h3 className="border-b pb-1 text-xs font-semibold uppercase text-muted-foreground">Công</h3>
        <div className="grid grid-cols-4 gap-1 text-center" data-testid="payslip-work">
          {tiles.map((t) => (
            <div key={t.sym} className={cn('rounded-md px-1 py-1.5', t.on ? CELL_CLASS[t.st] : 'bg-muted text-muted-foreground')} title={t.label}>
              <div className="text-[11px] font-semibold leading-none">{t.sym}</div>
              <div className="mt-1 text-base font-semibold leading-none tabular-nums">{t.value}</div>
            </div>
          ))}
        </div>
        {(sheet.lcn > 0 || sheet.late > 0) && (
          <p className="text-xs text-muted-foreground">
            {sheet.lcn > 0 && (
              <>
                Làm CN: <b className="tabular-nums">{sheet.lcn}</b> ngày.{' '}
              </>
            )}
            {sheet.late > 0 && (
              <>
                Trễ / về sớm: <b className="tabular-nums">{sheet.late}</b> lần.
              </>
            )}
          </p>
        )}
        {lv && (
          <div
            className={cn(
              'flex items-center justify-between gap-2 rounded-md px-3 py-1.5 text-sm',
              lv.accrued > 0 ? 'bg-success-soft text-success-foreground' : 'bg-muted text-muted-foreground',
            )}
            data-testid="payslip-leave"
          >
            <span>
              Phép năm tháng này
              {lv.accrued <= 0 && lv.reason && (
                <span className="ml-1 text-xs">
                  ({PAYSLIP_LEAVE_REASON_LABELS[lv.reason]}
                  {lv.detail ? `: ${lv.detail}` : ''})
                </span>
              )}
            </span>
            <span className="text-right">
              <b className="text-base tabular-nums">{lv.accrued > 0 ? `+${fmtWork(lv.accrued)}` : '0'}</b>
              {lv.closing !== null && <span className="block text-[11px]">còn {fmtWork(lv.closing)} ngày</span>}
            </span>
          </div>
        )}
      </section>

      <section className="space-y-1">
        <div className="grid grid-cols-7 gap-1 text-center text-xs" data-testid="payslip-calendar">
          {WEEK_MON_FIRST.map((w) => (
            <div key={w} className="font-medium text-muted-foreground">
              {WEEKDAY_SHORT[w]}
            </div>
          ))}
          {Array.from({ length: lead }, (_, k) => (
            <div key={`e${k}`} />
          ))}
          {sheet.days.map((x) => (
            <div
              key={x.d}
              title={`${x.d.split('-').reverse().join('/')}: ${ATTENDANCE_CELL_STATUS_LABELS[x.st]}${x.ot > 0 ? ` · tăng ca ${fmtWork(x.ot)}h` : ''}${x.marks.map((m) => ` · ${DAY_MARK_KIND_LABELS[m]}`).join('')}`}
              className={cn(
                'relative flex min-h-11 flex-col items-center rounded py-1 leading-none tabular-nums',
                CELL_CLASS[x.st],
                (x.paid || x.marks.length > 0) && 'ring-1 ring-inset ring-warning',
              )}
            >
              <span className="text-[10px] text-muted-foreground">{Number(x.d.slice(8))}</span>
              <span className="mt-0.5 min-h-3.5 text-[11px] font-semibold">{x.sym}</span>
              {x.ot > 0 && <span className="mt-0.5 text-[9px] font-semibold text-warning">{fmtOtBadge(x.ot)}</span>}
              {x.marks.length > 0 && (
                <span className="absolute bottom-0.5 left-0.5 flex gap-px" data-testid="payslip-mark">
                  {x.marks.map((m) => (
                    <span key={m} className={cn('inline-flex size-3 items-center justify-center rounded-sm text-[8px] font-bold leading-none', MARK_CLASS[m])}>
                      {DAY_MARK_SYMBOLS[m]}
                    </span>
                  ))}
                </span>
              )}
            </div>
          ))}
        </div>
        <AttendanceLegend marks />
        {live && <p className="text-[11px] text-muted-foreground">Kỳ này tính lương trước khi có tờ công: lịch lấy theo bảng công hiện tại.</p>}
      </section>

      <section data-testid="payslip-extras">
        <h3 className="border-b pb-1 text-xs font-semibold uppercase text-muted-foreground">Tăng ca · Về trễ · Hỗ trợ · Qua đêm</h3>
        {PAYSLIP_EXTRA_KEYS.map((k) => {
          const e = sheet.extras[k];
          const amount = e.amount ?? (k === 'ot' ? officeOt : null);
          const qty = e.qty > 0 ? `${fmtWork(e.qty)}${e.unit === 'h' ? 'h' : ' ngày'}` : '–';
          const head = (
            <span className="flex flex-1 items-center justify-between gap-3">
              <span>
                {PAYSLIP_EXTRA_LABELS[k]} <span className="text-xs text-muted-foreground tabular-nums">({qty})</span>
              </span>
              <span className="tabular-nums">{amount ? formatMoney(amount) : '–'}</span>
            </span>
          );
          return e.notes.length ? (
            <details key={k} className="group py-1" data-testid={`payslip-extra-${k}`}>
              <summary className="flex cursor-pointer list-none items-center gap-1 marker:hidden">
                <span className="text-xs text-muted-foreground transition-transform group-open:rotate-90">›</span>
                {head}
              </summary>
              <ul className="mt-1 ml-4 list-disc pl-3 text-xs text-muted-foreground">
                {e.notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            </details>
          ) : (
            <div key={k} className="flex items-center gap-1 py-1" data-testid={`payslip-extra-${k}`}>
              <span className="w-2" />
              {head}
            </div>
          );
        })}
        <p className="text-[11px] text-muted-foreground">
          Số tiền các dòng này đã nằm trong khoản Thu nhập bên dưới (tăng ca, tăng ca tài xế, công tác phí), không cộng thêm lần nữa.
        </p>
      </section>
    </div>
  );
}
